// T013, T018, T021, T024, T025 — Wallet detail page tying everything together.
import { useCallback, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import useAdminWallet from "../../hooks/useAdminWallet";
import useAdminWalletTransactions from "../../hooks/useAdminWalletTransactions";
import {
  adminIssueCredit,
  adminRevokeCredit,
} from "../../services/wallet/adminWalletApi";
import type {
  AdminIssueRequest,
  AdminRevokeRequest,
} from "../../types/wallet";
import { formatEur } from "../../utils/eur";

import WalletSummaryCards from "./components/WalletSummaryCards";
import UpcomingExpirationsList from "./components/UpcomingExpirationsList";
import TransactionsTable from "./components/TransactionsTable";
import IssueCreditDialog from "./components/IssueCreditDialog";
import RevokeCreditDialog from "./components/RevokeCreditDialog";
import type { AuditFilterValue } from "./components/AdminAuditFilter";

const WalletDetailsPage = () => {
  const params = useParams<{ emaid: string }>();
  const accountId = params.emaid ? decodeURIComponent(params.emaid) : undefined;

  const toast = useRef<Toast>(null);
  const { wallet, transactions, nextCursor, loading, error, refresh, applyWalletState } =
    useAdminWallet(accountId);
  const txState = useAdminWalletTransactions(accountId, transactions, nextCursor);

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [auditFilter, setAuditFilter] = useState<AuditFilterValue>("all");

  const ownerLabel = wallet?.account_id ?? accountId ?? "";

  const handleIssueSubmit = useCallback(
    async ({ request }: { accountId: string; request: AdminIssueRequest }) => {
      if (!accountId) return;
      setIssueSubmitting(true);
      try {
        const response = await adminIssueCredit(accountId, request);
        if (response.wallet) {
          applyWalletState(response.wallet);
        }
        await refresh();
        if (response.idempotent_replay) {
          toast.current?.show({
            severity: "warn",
            summary: "Nothing new credited",
            detail: `This issuance was already processed, so no additional credits were issued to ${ownerLabel}. Check the transaction history before issuing again.`,
            life: 6000,
          });
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Credits issued",
            detail: `Issued ${formatEur(request.amount)} to ${ownerLabel}.`,
            life: 4000,
          });
        }
        setIssueDialogOpen(false);
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ??
          "Failed to issue credits. Check the form and try again.";
        toast.current?.show({
          severity: "error",
          summary: "Issuance failed",
          detail,
          life: 5000,
        });
      } finally {
        setIssueSubmitting(false);
      }
    },
    [accountId, applyWalletState, ownerLabel, refresh],
  );

  const handleRevokeSubmit = useCallback(
    async (request: AdminRevokeRequest) => {
      if (!accountId) return;
      setRevokeSubmitting(true);
      try {
        const response = await adminRevokeCredit(accountId, request);
        await refresh();

        const requested = response.requested_amount;
        const applied = response.applied_amount;
        // The backend caps the amount at what can be taken: the targeted
        // batch's remaining credit (and the balance), or the balance when no
        // batch was picked. Compare numerically: "5.0" and "5.00" are equal.
        const limited = Number(applied) < Number(requested);
        const limitedTo = request.target_batch_id
          ? "the credit remaining in the selected batch"
          : "the available balance";
        toast.current?.show({
          severity: limited ? "warn" : "success",
          summary: limited ? "Revocation limited" : "Credits revoked",
          detail: limited
            ? `Revoked ${formatEur(applied)} of ${formatEur(requested)} requested from ${ownerLabel}, limited to ${limitedTo}.`
            : `Revoked ${formatEur(applied)} from ${ownerLabel}.`,
          life: 5000,
        });
        setRevokeDialogOpen(false);
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ??
          "Failed to revoke credits. Check the form and try again.";
        toast.current?.show({
          severity: "error",
          summary: "Revocation failed",
          detail,
          life: 5000,
        });
      } finally {
        setRevokeSubmitting(false);
      }
    },
    [accountId, ownerLabel, refresh],
  );

  // The Toast and the issue dialog sit outside the state-dependent body so
  // they survive the switch from "no wallet yet" to the wallet view that a
  // successful first issuance triggers.
  const renderBody = () => {
    if (loading && !wallet) {
      return (
        <div className="p-4 flex flex-column align-items-center gap-3">
          <ProgressSpinner />
          <span className="text-color-secondary">Loading wallet…</span>
        </div>
      );
    }

    if (error === "forbidden") {
      return (
        <div className="p-4">
          <Message
            severity="error"
            text="You do not have permission to view this wallet."
          />
          <div className="mt-3">
            <Link to="/wallet-admin">← Back to wallet search</Link>
          </div>
        </div>
      );
    }

    // T018 — the account has never received credit, so there is no wallet to
    // show. Issuing creates one; the success handler reloads this page's data.
    if (error === "no_wallet") {
      return (
        <div className="p-4 wallet-admin-page">
          <Link to="/wallet-admin" className="text-sm">← Back to wallet search</Link>
          <h2 className="m-0 mt-2 mb-4">Wallet · {ownerLabel}</h2>
          <Message
            severity="info"
            className="w-full mb-4"
            text="This account has no wallet yet: it has never received credit. Issuing credits will create one."
          />
          <Button
            label="Issue credit"
            icon="pi pi-plus-circle"
            onClick={() => setIssueDialogOpen(true)}
          />
        </div>
      );
    }

    if (error === "not_found") {
      return (
        <div className="p-4">
          <Message severity="warn" text="No CoCharge account found for that ID." />
          <div className="mt-3">
            <Link to="/wallet-admin">← Back to wallet search</Link>
          </div>
        </div>
      );
    }

    if (error === "unknown" || !wallet) {
      return (
        <div className="p-4">
          <Message
            severity="error"
            text="Failed to load this wallet. Try again or check the Payment service."
          />
          <div className="mt-3 flex gap-2">
            <Button label="Retry" icon="pi pi-refresh" onClick={refresh} />
            <Link to="/wallet-admin" className="align-self-center">← Back to wallet search</Link>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 wallet-admin-page">
        <div className="flex flex-column sm:flex-row sm:align-items-center sm:justify-content-between gap-3 mb-4">
          <div>
            <Link to="/wallet-admin" className="text-sm">← Back to wallet search</Link>
            <h2 className="m-0 mt-2">Wallet · {ownerLabel}</h2>
          </div>
          <div className="flex gap-2">
            <Button
              label="Issue credits"
              icon="pi pi-plus-circle"
              onClick={() => setIssueDialogOpen(true)}
            />
            <Button
              label="Revoke credits"
              icon="pi pi-user-minus"
              severity="danger"
              onClick={() => setRevokeDialogOpen(true)}
              disabled={Number(wallet.spendable_balance) <= 0}
            />
          </div>
        </div>

        <WalletSummaryCards wallet={wallet} />

        <div className="mt-4">
          <UpcomingExpirationsList expirations={wallet.upcoming_expirations ?? []} />
        </div>

        <div className="mt-4">
          <TransactionsTable
            items={txState.items}
            loading={txState.loading}
            hasMore={txState.hasMore}
            onLoadMore={txState.loadMore}
            filter={auditFilter}
            onFilterChange={setAuditFilter}
          />
        </div>

        <RevokeCreditDialog
          visible={revokeDialogOpen}
          email={ownerLabel}
          spendableBalance={wallet.spendable_balance}
          upcomingExpirations={wallet.upcoming_expirations ?? []}
          loading={revokeSubmitting}
          onSubmit={handleRevokeSubmit}
          onHide={() => !revokeSubmitting && setRevokeDialogOpen(false)}
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      {renderBody()}
      <IssueCreditDialog
        visible={issueDialogOpen}
        fixedAccount={accountId ? { accountId, email: ownerLabel } : undefined}
        loading={issueSubmitting}
        onSubmit={handleIssueSubmit}
        onHide={() => !issueSubmitting && setIssueDialogOpen(false)}
      />
    </>
  );
};

export default WalletDetailsPage;
